import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DataLoader, PageRequest } from '@aiz-gl/omni-ui';
import { firstValueFrom, timeout } from 'rxjs';
import {
  ConfigMappingResponse,
  CreateRmaRequest,
  CreateRmaResponse,
  MyWarehouseChannelsResponse,
  PaginatedRmaByAtbOrderResponse,
  PaginatedRmaSearchResponse,
  RmaByAtbOrderRequest,
  RmaDecisionRequest,
  RmaDetailResponse,
  RmaResubmitRequest,
  RmaSearchResponse,
  RmaStatusCount,
  RmaStatusCountsRequest,
  RmaSearchRequest,
} from '../../models/rma.models';
import { mapRmaSearchResponseToRow, RmaSummaryRow } from './rma.mapper';
import { RegionService } from '../region/region.service';

@Injectable({
  providedIn: 'root',
})
export class RmaService {
  private readonly http = inject(HttpClient);
  private readonly regions = inject(RegionService);

  private toFilterArray(value: string[] | string | null | undefined): string[] {
    if (Array.isArray(value)) {
      return value.filter((entry) => typeof entry === 'string' && entry.trim().length > 0);
    }

    if (typeof value === 'string' && value.trim().length > 0) {
      return [value];
    }

    return [];
  }

  rmaDataLoader: DataLoader<RmaSummaryRow> = (request: PageRequest) => {
    const filters = (request.filter ?? {}) as Partial<RmaSearchRequest>;
    const regionId = this.regions.selectedRegionId() ?? 0;
    const payload: RmaSearchRequest = {
      regionId,
      pageNumber: request.pageNumber,
      pageSize: request.pageSize,
      sortBy: request.sortColumn ?? '',
      sortDirection: request.sortDirection ?? '',
      isMyRmas: Boolean(filters.isMyRmas),
      rmaNumber: filters.rmaNumber ?? '',
      erpRmaNumber: filters.erpRmaNumber ?? '',
      atbId: filters.atbId ?? '',
      rmaType: this.toFilterArray(filters.rmaType),
      status: this.toFilterArray(filters.status),
      startDate: filters.startDate ?? '',
      endDate: filters.endDate ?? '',
      rmaComplianceStatus: this.toFilterArray(filters.rmaComplianceStatus),
      orderNumber: filters.orderNumber ?? '',
      shipmentNumber: filters.shipmentNumber ?? '',
      salesChannelCode: this.toFilterArray(filters.salesChannelCode),
      dccCode: this.toFilterArray(filters.dccCode),
      warehouseCode: this.toFilterArray(filters.warehouseCode),
    };

    return firstValueFrom(
      this.http.post<PaginatedRmaSearchResponse>('RMA/search', payload),
    ).then((response) => ({
      data: response.items.map(mapRmaSearchResponseToRow),
      totalRecords: response.total_count,
      pageNumber: response.page_number,
      pageSize: response.page_size,
    }));
  };

  getStatusCounts(payload: RmaStatusCountsRequest): Promise<RmaStatusCount[]> {
    const payloadWithRegion = {
      ...payload,
      regionId: this.regions.selectedRegionId() ?? 0,
    };

    return firstValueFrom(
      this.http.post<RmaStatusCount[]>('RMA/status-counts', payloadWithRegion),
    );
  }

  getPendingApprovalCountForMyRmas(regionId?: number): Promise<number> {
    const payload: RmaSearchRequest = {
      regionId: regionId ?? this.regions.selectedRegionId() ?? 0,
      pageNumber: 1,
      pageSize: 10,
      sortBy: '',
      sortDirection: 'indet',
      isMyRmas: true,
      rmaNumber: '',
      erpRmaNumber: '',
      atbId: '',
      rmaType: [],
      status: ['Under Review'],
      startDate: '',
      endDate: '',
      rmaComplianceStatus: [],
      orderNumber: '',
      shipmentNumber: '',
      salesChannelCode: [],
      dccCode: [],
      warehouseCode: [],
    };

    return firstValueFrom(
      this.http.post<PaginatedRmaSearchResponse>('RMA/search', payload),
    ).then((response) => response.total_count || 0);
  }

  getRmaById(rmaId: number) {
    return this.http.get<RmaDetailResponse>(`RMA/${rmaId}`);
  }

  getRmaActivityLogs(rmaNumber: string) {
    return this.http.get<unknown>(`RMA/${encodeURIComponent(rmaNumber)}/activity-logs`);
  }

  getRmasByAtbOrder(payload: RmaByAtbOrderRequest): Promise<PaginatedRmaByAtbOrderResponse> {
    return firstValueFrom(
      this.http.post<PaginatedRmaByAtbOrderResponse>('RMA/by-atb-order', payload),
    );
  }

  createRma(payload: CreateRmaRequest): Promise<CreateRmaResponse> {
    return firstValueFrom(this.http.post<CreateRmaResponse>('RMA', payload));
  }

  submitRmaDecision(rmaId: number, payload: RmaDecisionRequest): Promise<unknown> {
    return firstValueFrom(this.http.post(`RMA/${rmaId}/decision`, payload));
  }

  getRmaResubmitReasons(): Promise<ConfigMappingResponse[]> {
    return firstValueFrom(
      this.http.get<ConfigMappingResponse[]>('ConfigMapping?map_group=RmaResubmitReason'),
    );
  }

  getRmaReturnReasons(): Promise<ConfigMappingResponse[]> {
    return firstValueFrom(
      this.http.get<ConfigMappingResponse[]>('ConfigMapping?map_group=RMAReasonCode'),
    );
  }

  resubmitRma(rmaId: number, payload: RmaResubmitRequest): Promise<unknown> {
    return firstValueFrom(this.http.post(`RMA/${rmaId}/resubmit`, payload));
  }

  uploadRmaAttachments(rmaId: number, files: File[]): Promise<unknown> {
    const formData = new FormData();
    for (const file of files) {
      formData.append('Attachments', file, file.name);
    }

    return firstValueFrom(
      this.http.post(`RMA/${rmaId}/attachments`, formData),
    );
  }

  getRmaAttachments(rmaId: number) {
    return this.http.get<unknown>(`RMA/${rmaId}/attachments`);
  }

  downloadRmaAttachment(rmaId: number, attachmentId: string) {
    return this.http.get(
      `RMA/${rmaId}/attachments/${encodeURIComponent(attachmentId)}`,
      {
        responseType: 'blob' as const,
        observe: 'response' as const,
      },
    );
  }

  getMyWarehouseChannels(): Promise<MyWarehouseChannelsResponse> {
    return firstValueFrom(
      this.http
        .get<MyWarehouseChannelsResponse>('RMA/my-warehouse-channels')
        .pipe(timeout({ first: 3000 })),
    );
  }
}

